<template>
  <div class="relative">
    <div
      v-if="item.disabled"
      class="absolute inset-0 z-[5] cursor-not-allowed"
      style="background: rgba(255,255,255,0.5)"
    />
    <el-form
      @submit.prevent="validateSubmit('itemForm')"
      novalidate
      @keypress.enter.prevent=""
      :model="item"
      :rules="rules"
      label-position="top"
      ref="itemForm"
      class="flex flex-col items-center"
    >
      <div class="input-section relative z-[10]">
        <div class="flex flex-col md:flex-row justify-end">
          <div class="md:w-3/12 m-2">
            <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('itemForm')"
            />
          </div>

          <div class="md:w-3/12 m-2" v-if="enableClearForm">
            <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('itemForm')"
            />
          </div>
        </div>
      </div>

      <!-- @submit errors -->
      <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
          () => {
            error.title = ''
            error.body = ''
          }
        "
      />

      <!-- Item Name -->
      <div class="input-section">
        <h1>{{ t('sections.itemInfo') }}</h1>

        <div class="input-group">
            <el-form-item :label="t('name')" prop="name" class="input-label" style="margin: 5px auto">
            <el-input
              id="name"
              v-model="item.name"
              :placeholder="t('placeholders.item')"
              maxlength="255"
              show-word-limit
              clearable
              required
            >
            </el-input>
          </el-form-item>
          <el-form-item :label="t('receiptName')" prop="receiptName" class="input-label" style="margin: 5px auto">
            <el-input
              id="receipt-name"
              v-model="item.receiptName"
              :placeholder="t('placeholders.receiptName')"
              maxlength="255"
              show-word-limit
              clearable
              @focus="() => {
                if (!item.receiptName) {
                  item.receiptName = item.name
                }
              }"
            >
            </el-input>
          </el-form-item>
        </div>
      
      </div>

      <!-- pricing -->
      <div class="input-section">
        <!-- <h1>{{ t('sections.pricing') }}</h1> -->
        <div class="input-group">
          <el-form-item :label="t('priceType')" class="input-label" style="margin: 5px auto">
            <el-select v-model="item.priceType" filterable class="w-full">
              <el-option v-for="pt in ['static', 'dynamic']" :key="pt" :label="t(pt)" :value="pt">
                <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                  {{ t(pt) }}
                </span>
              </el-option>
            </el-select>
          </el-form-item>
<!-- 
          <el-form-item
            :label="t('listPriceAmount')"
            prop="listPriceAmount"
            class="input-label"
            style="margin: 5px auto"
          >
            <money-input
              id="list-price"
              :currency="$ctx.currency"
              v-model="item.listPriceAmount"
              @change="updatePrice($event, 'listPriceAmount')"
              @dinero-created="(dinero) => (item.listPriceDinero = dinero)"
              ref="listPriceAmountRef"
            />
          </el-form-item> -->

          <el-form-item
            :label="t('salePriceAmount')"
            prop="salePriceAmount"
            class="input-label"
            style="margin: 5px auto"
          >
            <money-input
              id="sale-price"
              @focusin="updateSalePrice"
              :currency="$ctx.currency"
              :show-currency-prefix="true"
              v-model="item.salePriceAmount"
              @change="updatePrice($event, 'salePriceAmount')"
              @dinero-created="(dinero) => (item.salePriceDinero = dinero)"
              ref="salePriceAmountRef"
            />
          </el-form-item>

          <!-- <el-form-item
            :label="t('wholesalePriceAmount')"
            prop="wholesalePriceAmount"
            class="input-label"
          >
            <money-input
              id="wholesale-price"
              :currency="$ctx.currency"
              v-model="item.wholesalePriceAmount"
              @dinero-created="dinero => (item.wholesalePriceDinero = dinero)"
              ref="wholesalePriceAmountRef"
            />
          </el-form-item> -->

          <el-form-item
            v-if="$can('get', '/inventory/items/cost')"
            :label="t('cost')"
            prop="costAmount"
            class="input-label"
            style="margin: 5px auto"
          >
            <money-input
              id="cost"
              :currency="$ctx.currency"
              :show-currency-prefix="true"
              v-model="item.costAmount"
              @change="updatePrice($event, 'costAmount')"
              @dinero-created="(dinero) => (item.costDinero = dinero)"
              ref="costAmountRef"
            />
          </el-form-item>
        </div>
      </div>

      <div class="input-section">
        <!-- <h1>{{ t('sections.stockInfo') }}</h1> -->
        <div class="input-group">
          <el-form-item
            :label="t('barcode')"
            prop="barcode"
            class="input-label"
            style="margin: 5px auto"
          >
            <div class="flex gap-1 items-center w-full">
              <el-tooltip
                  class="item"
                  effect="dark"
                  :content="t('tooltips.barcode')"
                  placement="bottom"
              >
                <el-input
                    id="barcode"
                    v-model="item.barcode"
                    :placeholder="t('placeholders.barcode')"
                    maxlength="63"
                    clearable
                    @keypress.enter.prevent=""
                >
                </el-input>
              </el-tooltip>
              <BarcodeGenerator @onBarcode="(newBarcode) => { item.barcode = newBarcode}"/>
            </div>
            <!-- Multi-barcode input - directly under barcode input -->
            <div class="mt-2 w-full">
              <MultiBarcodeInput
                v-model="item.itemBarcodes"
              />
            </div>
          </el-form-item>

          <el-form-item :label="t('sku')" prop="sku" class="input-label" style="margin: 5px auto">
            <el-tooltip class="item" effect="dark" :content="t('tooltips.sku')" placement="bottom">
              <el-input
                id="sku"
                v-model="item.sku"
                :placeholder="t('placeholders.sku')"
                maxlength="63"
                clearable
              >
              </el-input>
            </el-tooltip>
          </el-form-item>
        </div>
      </div>

      <div class="input-section">
        <!-- <h1>{{ t('sections.options') }}</h1> -->

        <div class="input-group">
          <el-form-item prop="isSerialized" class="input-label" style="margin: 5px auto">
            <el-tooltip
              class="item"
              effect="dark"
              :content="t('tooltips.serialized')"
              placement="bottom"
            >
              <el-checkbox v-model="item.isSerialized" id="is-serialized">
                {{ t('isSerialized') }}
              </el-checkbox>
            </el-tooltip>
          </el-form-item>

          <el-form-item prop="unlimitedStock" class="input-label" style="margin: 5px auto">
            <el-tooltip
              class="item"
              effect="dark"
              :content="t('tooltips.unlimitedStock')"
              placement="bottom"
            >
              <el-checkbox v-model="item.unlimitedStock" id="unlimited-stock">
                {{ t('unlimitedStock') }}
              </el-checkbox>
            </el-tooltip>
          </el-form-item>

          <el-form-item prop="sellOutOfStock" class="input-label" style="margin: 5px auto">
            <el-checkbox
              v-model="item.sellOutOfStock"
              id="sell-out-of-stock"
              :disabled="item.isSerialized || item.unlimitedStock"
            >
              {{ t('sellOutOfStock') }}
            </el-checkbox>
          </el-form-item>

          <el-form-item prop="discountEligible" class="input-label" style="margin: 5px auto">
            <el-tooltip
              class="item"
              effect="dark"
              :content="t('tooltips.discountEligible')"
              placement="bottom"
            >
              <el-checkbox v-model="item.discountEligible" id="discount-eligible">
                {{ t('discountEligible') }}
              </el-checkbox>
            </el-tooltip>
          </el-form-item>

          <el-form-item prop="disabled" class="input-label relative z-[10]" style="margin: 5px auto">
            <el-checkbox
              v-model="item.disabled"
              id="disabled"
              :class="{ 'disabled-active': item.disabled }"
            >
              <span :class="item.disabled ? 'text-red-600 font-semibold' : ''">{{ t('disabled') }}</span>
            </el-checkbox>
          </el-form-item>
          <el-form-item v-if="settingsStore.getValue('expiration_module') === 'true'" prop="isExpirable" class="input-label" style="margin: 5px auto">
            <el-checkbox v-model="item.isExpirable" id="is-expirable">
              {{ t('isExpirable') }}
            </el-checkbox>
          </el-form-item>
        </div>
      </div>
<!--      <div-->
<!--        v-if="Array.isArray(item.customPrices) && item.customPrices.length"-->
<!--        class="input-section"-->
<!--      >-->
<!--        &lt;!&ndash; <h1 class="mb-2">{{ t('customPrices') }}</h1> &ndash;&gt;-->

<!--        <div class="grid grid-cols-1 md:grid-cols-4 gap-2">-->
<!--          <el-form-item-->
<!--            :label="$ctx.locale == 'en' ? cp.customPriceType?.enName : cp.customPriceType?.arName"-->
<!--            v-for="cp of item.customPrices"-->
<!--            :key="cp"-->
<!--            class="input-label"-->
<!--            style="margin: 5px 0"-->
<!--          >-->
<!--            <money-input-->
<!--              class="col-span-4"-->
<!--              :currency="$ctx.currency"-->
<!--              v-model="cp.priceAmount"-->
<!--              @change="updateVariantCustomPrice($event, cp)"-->
<!--              @dinero-created="(dinero) => (cp.priceDinero = dinero)"-->
<!--            />-->
<!--          </el-form-item>-->
<!--        </div>-->
<!--      </div>-->

     
      <div class="input-section">
        <!-- <h1>{{ t('sections.descriptiveInfo') }}</h1> -->


        <div class="input-group">
          <el-form-item
              :label="t('itemBrand')"
              prop="itemBrand.id"
              class="input-label"
              style="margin: 5px auto"
          >
            <el-select v-model="item.itemBrand.id" filterable clearable class="w-full">
              <el-option
                  v-for="brand in brands"
                  :key="brand.id"
                  :label="brand.name"
                  :value="brand.id"
              >
                <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                  {{ brand.name }}
                </span>
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item
              :label="t('itemCategory')"
              prop="itemCategory.id"
              class="input-label"
              style="margin: 5px auto"
          >
            <el-tree-select
              v-model="item.itemCategory.id"
              popper-class="rtl"
              :data="categoriesTree"
              filterable
              check-strictly
              :render-after-expand="false"
              show-checkbox
              check-on-click-node
            />
          </el-form-item>

          <el-form-item :label="t('itemTags')" class="input-label" style="margin: 5px auto">
            <el-select
                v-model="selectedTags"
                filterable
                clearable
                multiple
                class="w-full"
                @change="updateSelectedTags"
            >
              <el-option v-for="tag in tags" :key="tag.id" :label="tag.name" :value="tag.id">
              <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                {{ tag.name }}
              </span>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            :label="t('vendor')"
            prop="vendor.id"
            class="input-label"
            style="margin: 5px auto"
        >
          <el-select v-model="item.vendor.id" filterable clearable class="w-full">
            <el-option
                v-for="vendor in vendors"
                :key="vendor.id"
                :label="vendor.name"
                :value="vendor.id"
            >
                <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                  {{ vendor.name }}
                </span>
            </el-option>
          </el-select>
        </el-form-item>
        </div>
      
        <el-form-item
          :label="t('description')"
          prop="description"
          class="input-label"
          style="margin: 5px auto"
        >
          <el-input
            id="description"
            v-model="item.description"
            :placeholder="t('placeholders.description')"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 8 }"
            clearable
          >
          </el-input>
        </el-form-item>


      </div>

      <div class="input-section">
        <div class="input-group">
          <el-form-item v-if="!item.id" :label="t('quantityInHand')" prop="requestOnlyQuantityInHand" class="input-label" style="margin: 5px 0">
            <div class="inline w-full">
              <el-input
                  id="requestOnlyQuantityInHand"
                  v-model.number="item.requestOnlyQuantityInHand"
                  type="number"
                  min="0"
                  style="--el-input-bg-color: #EFFFCB"
                  :disabled="item.unlimitedStock"
                  @wheel="(e: WheelEvent) => { (e.currentTarget as HTMLElement).querySelector('input')?.blur() }"
                  @input="(e) => {if (e < 0) item.requestOnlyQuantityInHand = 0}"
              >
              </el-input>
              <tag-input
                  v-if="item.isSerialized"
                  v-model="item.requestOnlyQuantityInHandItemSerials"
                  :insertFunction="(val) => item.insertSerialNumber(val)"
                  :removeFunction="(index) => item.removeSerialNumber(index)"
                  :valueFormatter="(serialObject) => serialObject.itemSerial.number"
                  :validate="() => item.validateSerials()"
              />
            </div>
        </el-form-item>
        <el-form-item :label="t('alertWhenQTYIs')" prop="minQty" :class="['input-label', { 'min-qty-input': !item.unlimitedStock }]" style="margin: 5px 0">
          <el-input
                id="minQty"
                v-model.number="item.minQty"
                type="number"
                min="0"
                style="--el-input-bg-color: #FFFDE1"
                :disabled="item.unlimitedStock"
                @wheel="(e: WheelEvent) => { (e.currentTarget as HTMLElement).querySelector('input')?.blur() }"
                @input="(e: any) => {if (e === '' || e == null) item.minQty = null; else if (e < 0) item.minQty = 0}"
            >
            </el-input>
        </el-form-item>
        </div>
        <div class="input-group" v-if="item.isExpirable && item.requestOnlyQuantityInHand > 0">
          <el-form-item :label="t('batchNumber')" prop="requestOnlyBatchNumber" class="input-label" style="margin: 5px 0">
            <template #label>
              <span>
                <span class="text-red-500">*</span>
                {{ t('batchNumber') }}
              </span>
            </template>
            <el-input
              id="requestOnlyBatchNumber"
              v-model="item.requestOnlyBatchNumber"
            />
        </el-form-item>
        <el-form-item :label="t('manufactureDate')" prop="requestOnlyManufactureDate" class="input-label" style="margin: 5px 0">
          <el-date-picker 
            v-model="item.requestOnlyManufactureDate"
            type="date"
            format="DD-MM-YYYY"
            style="width: 100%"
            :disabled-date="(date) => date > new Date()"
          />
        </el-form-item>
        <el-form-item :label="t('expiryDate')" prop="requestOnlyExpiryDate" class="input-label" style="margin: 5px 0">
          <template #label>
              <span>
                <span class="text-red-500">*</span>
                {{ t('expiryDate') }}
              </span>
            </template>
          <el-date-picker
            v-model="item.requestOnlyExpiryDate"
            type="date"
            format="DD-MM-YYYY"
            style="width: 100%"
            :disabled-date="(date) => item.requestOnlyManufactureDate ? date < new Date(item.requestOnlyManufactureDate) : false"
          />
        </el-form-item>
        </div>

      </div>

      
      <CustomPriceSection v-model="item.itemCustomPrices"></CustomPriceSection>

      <PacksSection v-model="item.itemPacks"></PacksSection>

      <div
        v-if="item.id == null || item.hasVariants"
        class="variant-section grid grid-cols-1 input-section"
      >
        <div class="flex gap-2" v-if="attributeVariants.length">
          <h1>{{ t('variants') }}</h1>
        </div>
        <template v-if="attributeVariants.length">
          <div class="grid grid-cols-12">
            <div class="col-span-2 py-2">{{ t('attribute') }}</div>
            <div class="col-span-10 py-2">{{ t('variants') }}</div>
          </div>
          <div v-for="(attribute, i) in attributeVariants" :key="i" class="grid grid-cols-12 gap-2">
            <div class="capitalize py-2 my-2 col-span-2">
              {{ $ctx.locale == 'ar' ? attribute.arName : attribute.enName }}
            </div>
            <div class="bg-white p-2 m-2 rounded-md col-span-10">
              <variants-input v-model="attribute.values" @value-changed="onAttributeValueChanged(attribute, $event)"></variants-input>
            </div>
          </div>
        </template>

        <div
          v-for="(itemVariant, i) in itemVariants"
          :key="i"
          class="mt-2 border border-gray-300 rounded-md p-2"
        >
          <div value="input-group">
            <h1>{{ `${t('variant')} ${i + 1}` }}</h1>
            <div class="input-group">
              <el-form-item :label="t('name')" class="input-label" style="margin: 5px auto">
              <el-input class="read-only" readonly :value="itemVariant.name"> </el-input>
            </el-form-item>
            <el-form-item :label="t('receiptName')" prop="receiptName" class="input-label" style="margin: 5px auto">
              <el-input
                id="receipt-name"
                v-model="itemVariant.receiptName"
                :placeholder="t('placeholders.receiptName')"
                maxlength="255"
                show-word-limit
                clearable
                @focus="() => {
                  if (!itemVariant.receiptName) {
                    itemVariant.receiptName = itemVariant.name.replace(item.name, item.receiptName)
                  }
                }"
              >
              </el-input>
          </el-form-item>
            </div>
          </div>

          <div class="input-group">
            <el-form-item :label="t('priceType')" class="input-label" style="margin: 5px auto">
              <el-select v-model="itemVariant.priceType" filterable class="w-full">
                <el-option v-for="pt in ['static', 'dynamic']" :key="pt" :label="t(pt)" :value="pt">
                  <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                    {{ t(pt) }}
                  </span>
                </el-option>
              </el-select>
            </el-form-item>

            <!-- <el-form-item
              :label="t('listPriceAmount')"
              prop="listPriceAmount"
              class="input-label"
              style="margin: 5px auto"
            >
              <money-input
                id="list-price"
                :currency="$ctx.currency"
                v-model="itemVariant.listPriceAmount"
                @dinero-created="(dinero) => (itemVariant.listPriceDinero = dinero)"
                ref="listPriceAmountRef"
              />
            </el-form-item> -->

            <el-form-item
              :label="t('salePriceAmount')"
              prop="salePriceAmount"
              class="input-label"
              style="margin: 5px auto"
            >
              <money-input
                id="sale-price"
                :currency="$ctx.currency"
                v-model="itemVariant.salePriceAmount"
                @dinero-created="(dinero) => (itemVariant.salePriceDinero = dinero)"
              />
            </el-form-item>
            <el-form-item
              v-if="$can('get', '/inventory/items/cost')"
              :label="t('cost')"
              prop="costAmount"
              class="input-label"
              style="margin: 5px auto"
            >
              <money-input
                id="cost"
                :currency="$ctx.currency"
                v-model="itemVariant.costAmount"
                @dinero-created="(dinero) => (itemVariant.costDinero = dinero)"
              />
            </el-form-item>
          </div>
          <div class="input-group">
            <el-form-item
              :label="t('barcode')"
              prop="variant-barcode"
              class="input-label"
              style="margin: 5px auto"
            >
              <div class="flex gap-1 items-center w-full">
                <el-tooltip
                    class="item"
                    effect="dark"
                    :content="t('tooltips.barcode')"
                    placement="bottom"
                >
                  <el-input
                      id="variant-barcode"
                      v-model="itemVariant.barcode"
                      :placeholder="t('placeholders.barcode')"
                      maxlength="63"
                      clearable
                      @keypress.enter.prevent=""
                  >
                  </el-input>
                </el-tooltip>
                <BarcodeGenerator @onBarcode="(newBarcode) => { itemVariant.barcode = newBarcode}"/>
              </div>
              <!-- Multi-barcode input for variant - directly under barcode input -->
              <div class="mt-2 w-full">
                <MultiBarcodeInput
                  v-model="itemVariant.itemBarcodes"
                />
              </div>
            </el-form-item>

            <el-form-item
              :label="t('sku')"
              prop="variant-sku"
              class="input-label"
              style="margin: 5px auto"
            >
              <el-tooltip
                class="item"
                effect="dark"
                :content="t('tooltips.sku')"
                placement="bottom"
              >
                <el-input
                  id="variant-sku"
                  v-model="itemVariant.sku"
                  :placeholder="t('placeholders.sku')"
                  maxlength="63"
                  clearable
                >
                </el-input>
              </el-tooltip>
            </el-form-item>
          </div>
          <div class="input-group">
            <el-form-item prop="disabled" class="input-label" style="margin: 5px 0">
              <el-checkbox v-model="itemVariant.disabled" id="disabled">
                {{ t('disabled') }}
              </el-checkbox>
            </el-form-item>
          </div>
          <div class="input-group">
            <el-form-item v-if="!itemVariant.id" :label="t('quantityInHand')" prop="requestOnlyQuantityInHand" class="input-label" style="margin: 5px 0">
              <div class="inline w-full">
                <el-input
                    id="requestOnlyQuantityInHand"
                    v-model.number="itemVariant.requestOnlyQuantityInHand"
                    type="number"
                    min="0"
                    style="--el-input-bg-color: #EFFFCB"
                    :disabled="itemVariant.unlimitedStock"
                    @wheel="(e: WheelEvent) => { (e.currentTarget as HTMLElement).querySelector('input')?.blur() }"
                    @input="(e) => {if (e < 0) itemVariant.requestOnlyQuantityInHand = 0}"
                >
                </el-input>
                <tag-input
                    v-if="item.isSerialized"
                    v-model="itemVariant.requestOnlyQuantityInHandItemSerials"
                    :insertFunction="(val) => itemVariant.insertSerialNumber(val)"
                    :removeFunction="(index) => itemVariant.removeSerialNumber(index)"
                    :valueFormatter="(serialObject) => serialObject.itemSerial.number"
                    :validate="() => itemVariant.validateSerials()"
                />
              </div>
            </el-form-item>
            <el-form-item :label="t('alertWhenQTYIs')" prop="minQty" :class="['input-label', { 'min-qty-input': !itemVariant.unlimitedStock && !itemVariant.sellOutOfStock }]" style="margin: 5px 0">
              <el-input
                    id="minQty"
                    v-model.number="itemVariant.minQty"
                    type="number"
                    min="0"
                    style="--el-input-bg-color: #FFFDE1"
                    :disabled="itemVariant.unlimitedStock || itemVariant.sellOutOfStock"
                    @wheel="(e: WheelEvent) => { (e.currentTarget as HTMLElement).querySelector('input')?.blur() }"
                    @input="(e: any) => {if (e === '' || e == null) itemVariant.minQty = null; else if (e < 0) itemVariant.minQty = 0}"
                >
                </el-input>
            </el-form-item>
          </div>
          <div class="input-group" v-if="item.isExpirable && itemVariant.requestOnlyQuantityInHand > 0">
            <el-form-item :label="t('batchNumber')" prop="requestOnlyBatchNumber" class="input-label" style="margin: 5px 0">
                <template #label>
                    <span>
                      <span class="text-red-500">*</span>
                      {{ t('batchNumber') }}
                    </span>
                </template>
                <el-input
                  id="requestOnlyBatchNumber"
                  v-model="itemVariant.requestOnlyBatchNumber"
                />
              </el-form-item>
              <el-form-item :label="t('manufactureDate')" prop="requestOnlyManufactureDate" class="input-label" style="margin: 5px 0">
                <el-date-picker 
                  v-model="itemVariant.requestOnlyManufactureDate"
                  type="date"
                  format="DD-MM-YYYY"
                  style="width: 100%"
                  :disabled-date="(date) => date > new Date()"
                />
              </el-form-item>
              <el-form-item :label="t('expiryDate')" prop="requestOnlyExpiryDate" class="input-label" style="margin: 5px 0">
                <template #label>
                    <span>
                      <span class="text-red-500">*</span>
                      {{ t('expiryDate') }}
                    </span>
                </template>
                <el-date-picker
                  v-model="itemVariant.requestOnlyExpiryDate"
                  type="date"
                  format="DD-MM-YYYY"
                  style="width: 100%"
                  :disabled-date="(date) => itemVariant.requestOnlyManufactureDate ? date < new Date(itemVariant.requestOnlyManufactureDate) : false"
                />
              </el-form-item>
          </div>
          <CustomPriceSection custom-class="w-full"  v-model="itemVariant.itemCustomPrices"></CustomPriceSection>
          <div
            v-if="Array.isArray(itemVariant.customPrices) && itemVariant.customPrices.length"
            class="input-section"
          >
            <!-- <h1 class="mb-2">{{ t('customPrices') }}</h1> -->

            <div class="grid grid-cols-1 md:grid-cols-4 gap-2">
              <el-form-item
                :label="
                  $ctx.locale == 'en' ? cp.customPriceType?.enName : cp.customPriceType?.arName
                "
                v-for="cp of itemVariant.customPrices"
                :key="cp"
                class="input-label"
                style="margin: 5px auto"
              >
                <money-input
                  class="col-span-4"
                  :currency="$ctx.currency"
                  v-model="cp.priceAmount"
                  @dinero-created="(dinero) => (cp.priceDinero = dinero)"
                />
              </el-form-item>
            </div>
          </div>
          
          <PacksSection custom-class="w-full" v-model="itemVariant.itemPacks"></PacksSection>
          <div class="">
            <ItemImageManager :data="itemVariant" v-model="itemVariant.toUploadImages"></ItemImageManager>
          </div>
        </div>
      </div>

      <div class="input-section">
        <ItemImageManager :data="item" v-model="item.toUploadImages"></ItemImageManager>
      </div>

      <div class="input-section mt-4">
        <div class="flex flex-col md:flex-row justify-end">
          <div class="md:w-3/12 m-2">
            <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('itemForm')"
            />
          </div>

          <div class="md:w-3/12 m-2" v-if="enableClearForm">
            <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('itemForm')"
            />
          </div>
        </div>
      </div>
    </el-form>

    <!-- item info -->
    <div class="flex flex-col items-center border" v-if="item.id">
      <div class="input-section">
        <h1>{{ t('sections.generalInfo') }}</h1>

        <div class="input-label">
          <el-timeline>
            <el-timeline-item key="createdAt" :timestamp="item?.formatCreatedAt()">
              {{ $t('models.common.createdAt') }}
            </el-timeline-item>
            <el-timeline-item key="updatedAt" :timestamp="item?.formatUpdatedAt()">
              {{ $t('models.common.updatedAt') }}
            </el-timeline-item>
          </el-timeline>
        </div>
      </div>

      <div v-if="!item?.hasVariants" class="input-section">
        <h1>{{ t('sections.stockInfo') }}</h1>

        <el-table :data="item.itemStocks" style="width: 100%">
          <el-table-column type="expand" v-if="item.isSerialized">
            <template #default="scope">
              {{ t('serialNumbers') }}
              <li v-for="(serial, i) in scope.row.itemSerials" :key="i">
                <el-tag :key="i" type="info">{{ serial.number }}</el-tag>
              </li>
            </template>
          </el-table-column>
          <el-table-column
            :label="t('location')"
            :prop="$ctx.getDir() === 'rtl' ? 'location.arName' : 'location.enName'"
          >
          </el-table-column>
          <el-table-column :label="t('stockQuantity')" prop="quantity"> </el-table-column>
        </el-table>
      </div>
      <div v-else class="input-section">
        <h1>{{ t('sections.stockInfo') }}</h1>
        <div v-for="(variant, i) in item?.variants" :key="i">
          <el-table
            :show-header="i == 0"
            v-if="!variant?.unlimitedStock"
            :data="variant?.itemStocks"
            style="width: 100%"
          >
            <el-table-column type="expand" v-if="variant?.isSerialized">
              <template #default="scope">
                {{ t('serialNumbers') }}
                <li v-for="(serial, i) in scope.row.itemSerials" :key="i">
                  <el-tag :key="i" type="info">{{ serial.number }}</el-tag>
                </li>
              </template>
            </el-table-column>
            <el-table-column
              :label="t('location')"
              :prop="$ctx.getDir() === 'rtl' ? 'location.arName' : 'location.enName'"
            >
            </el-table-column>
            <el-table-column :label="t('name')">
              <template #default>
                {{ variant.name }}
              </template>
            </el-table-column>
            <el-table-column :label="t('stockQuantity')" prop="quantity"> </el-table-column>
          </el-table>
          <div class="text-lg" v-else>
            {{ t('unlimitedStock') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h } from 'vue'

// models
import Item, { itemI18nMessages } from '@/models/inventory/Item'
import Tag from '@/models/inventory/Tag'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import {ElForm, UploadUserFile} from 'element-plus'
import MoneyInput from '@/components/form/MoneyInput.vue'

import Category from '@/models/inventory/Category'
import Brand from '@/models/inventory/Brand'
import Vendor from '@/models/vendor/Vendor'
import Attribute from '@/models/inventory/Attribute'

import { useI18n } from 'vue-i18n'
import CustomPriceType from '@/models/inventory/CustomPrice'
import VariantsInput from './VariantsInput.vue'
import PacksSection from "@/views/inventory/items/PacksSection.vue";
import { ElMessageBox } from 'element-plus'
import ItemToSerializedContent
  from "@/views/inventory/items/ItemToSerializedContent.vue";
import CustomPriceSection from "@/views/inventory/items/CustomPriceSection.vue";
import ItemCustomPrice from "@/models/inventory/ItemCustomPrice";
import ItemImageManager from "@/components/file-manager/ItemImageManager.vue";
import TagInput from "@/components/form/TagInput.vue";
import {localizeFieldName} from "@/plugins/i18n";
import {
  inventoryReceivingI18nMessages
} from "@/models/inventory/InventoryReceiving";
import BarcodeGenerator from "@/views/inventory/items/BarcodeGenerator.vue";
import MultiBarcodeInput from "@/views/inventory/items/MultiBarcodeInput.vue";
import { useSettingsStore } from '@/stores/settingsStore'

interface Option {
  name: string
  values: string[]
}

export default defineComponent({
  name: 'simple-item-form',

  setup() {
    const messages = {
      en: {
        ...itemI18nMessages.en,

        sections: {
          itemInfo: 'Item Info',
          pricing: 'Pricing',
          options: 'Item options',
          stockInfo: 'Stock Info',
          descriptiveInfo: 'Descriptive Info',
          generalInfo: 'Item Info'
        },

        placeholders: {
          item: 'Example: backpack',
          description: 'Example: backpack with smooth fabric and life warranty',
          price: 'Example: 15000',
          cost: 'Example: 5000',
          barcode: 'Example: 1339620142715',
          barcodeDescription: 'Example: Supplier barcode',
          sku: 'Example: 2392',
          dragDrop: 'Drop file here or click to upload',
          fileTypes: 'jpg/png files with a size less than 500kb',
          receiptName: 'Receipt Name'
        },

        select: {
          skipVendor: 'Skip vendor',
          skipBrand: 'Skip Brand',
          skipCategory: 'Skip Category'
        },

        tooltips: {
          cost: 'Cost is used in accounting',
          serialized: 'This feature is used to track items individually',
          unlimitedStock: 'Services have unlimited stock',
          discountEligible: 'Sales reps can apply discount at checkout',
          barcode: 'Must be unique',
          sku: 'Must be unique, used for internal tracking purposes',
          tags: 'To select more than one tag, click Ctrl'
        },

        createItem: 'Save Item',
        location: 'Location',
        serialNumbers: 'Serial Numbers',
        stockQuantity: 'Stock Quanity',
        customPrices: 'Custom Prices',
        newCustomPrice: 'New Custom Price',
        customPriceName: 'Price Name',
        variant: 'Variant',
        variants: 'Variants',
        attribute: 'Attribute',
        thisItemHasQty: 'This item has qty',
        in: 'in',
        switchingItToSerializeMsg: 'Switching it to Serialize will adjust it quantity to 0.',
        doYouWantToMsg: 'Do you want to proceed?',
        warning: 'Warning',
        OK: 'OK',
        Cancel: 'Cancel',
        quantityInHand: "Quantity in hand",
        itemNameExists: "Item name exists. Do you want to continue?",
        duplicateBarcodeError: 'Duplicate barcode',
        alertWhenQTYIs: 'Alert when QTY is',
        isExpirable: 'Expires',
        batchNumber: 'Batch Number',
        expiryDate: 'Expiry Date',
        manufactureDate: 'Manufacture Date',
        errManufactureDateFuture: 'Manufacture date cannot be in the future',
        errExpiryBeforeManufacture: 'Expiry date cannot be before manufacture date',
      },

      ar: {
        ...itemI18nMessages.ar,

        sections: {
          itemInfo: 'معلومات المنتج',
          pricing: 'التسعيير',
          options: 'اعدادات المنتج',
          stockInfo: 'بيانات التخزين',
          descriptiveInfo: 'البيانات الوصفية',
          generalInfo: 'معلومات المنتج'
        },

        placeholders: {
          item: 'مثلا: حقيبة ظهر',
          description: 'مثلا: حقيبة ظهر بخام ناعم',
          price: 'مثلا: 15000',
          cost: 'مثلا: 5000',
          barcode: 'مثلا: 1339620142715',
          barcodeDescription: 'مثلا: باركود المورد',
          sku: 'مثلا: 2392',
          dragDrop: 'اسحب ملف الصورة هنا أو اضغط لاختيار ملف',
          fileTypes: 'الملفات المدعومة: jpeg png',
          receiptName: 'اسم المنتج في الفاتورة'
        },

        select: {
          skipVendor: 'تخطي الموزع',
          skipBrand: 'تخطي العلامة التجارية',
          skipCategory: 'تخطي المجموعة'
        },

        tooltips: {
          cost: 'يستخدم في حسابات الارباح وغيرها من التقارير',
          serialized: 'يستخدم الترقيم في متابعة المنتجات بشكل فردي',
          unlimitedStock: 'مثلا: الخدمات لاتخضع للمخزون',
          discountEligible: 'يمكن لمندوب المبيعات ان يقوم بتخفيض المنتجات عند البيع',
          barcode: 'يجب ان يكون فريدا',
          sku: 'يجب ان يكون فريدا - يستخدم كرقم تخزين داخلي',
          tags: 'اضغط على ctrl لاختيار اكثر  من وسم'
        },

        createItem: 'حفظ المنتج',
        location: 'الموقع',
        serialNumbers: 'Serial Numbers',
        stockQuantity: 'الكمية',
        customPrices: 'الاسعار الاخرى',
        newCustomPrice: 'سعر جديد',
        customPriceName: 'عنوان السعر',
        variant: 'نسخة',
        variants: 'النسخ',
        attribute: 'الصنف',
        thisItemHasQty: 'هذا المنتج لديه كمية',
        in: 'في',
        switchingItToSerializeMsg: 'تحديث نوعة الى تسلسلي مرقم سوف يعدل كميتة الى 0',
        doYouWantToMsg: 'هل ترغب بالمتابعة؟',
        warning: 'تنبيه',
        OK: 'موافق',
        Cancel: 'الغاء',
        quantityInHand: "الكمية الموجودة حاليا",
        itemNameExists: "اسم العنصر مستخدم. هل تريد المتابعة؟",
        duplicateBarcodeError: 'باركود مكرر',
        alertWhenQTYIs: 'تنبيه عندما تكون الكمية',
        isExpirable: 'تنتهي صلاحيتة',
        batchNumber: 'رقم الوجبة',
        expiryDate: 'تاريخ الانتهاء',
        manufactureDate: 'تاريخ الصنع',
        errManufactureDateFuture: 'تاريخ الصنع لا يمكن أن يكون في المستقبل',
        errExpiryBeforeManufacture: 'تاريخ الإنتهاء لا يمكن أن يكون قبل تاريخ الصنع',
      }
    }

    const { t } = useI18n({ messages, useScope: 'global' })

    const rules = {
      name: [
        { required: true, message: t('validation.required') },
        { max: 255, message: t('validation.maxLength', { len: 255 }) }
      ],
      listPriceAmount: [{ required: false, message: t('validation.required') }],
      salePriceAmount: [{ required: true, message: t('validation.required') }],
      costAmount: [{ required: true, message: t('validation.required') }],
      barcode: [{ max: 63, message: t('validation.maxLength', { len: 63 }) }],
      sku: [{ max: 63, message: t('validation.maxLength', { len: 63 }) }]
    }

    const settingsStore = useSettingsStore();

    return { t, rules, ItemCustomPrice, settingsStore }
  },

  components: {
    BarcodeGenerator,
    MultiBarcodeInput,
    TagInput,
    ItemImageManager,
    CustomPriceSection,
    PacksSection,
    HorizontalButton,
    AlertBadge,
    MoneyInput,
    VariantsInput
  },
  props: {
    initItem: {
      type: Item,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      default: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },
  // I have the choice to use v-model but would make this code
  // incredibly complicated!
  // I chose to alias/copy props, even though it might be against vue style guide
  // the parent hence passes an initial copy of the item and variants and DOES NOT
  // expect those inital copies to be synchronized!
  data() {
    return {
      item: this.initItem,
      tags: new Array<Tag>(),
      selectedTags: new Array<string | undefined>(),
      categories: new Array<Category>(),
      categoriesTree: new Array<any>(),
      brands: new Array<Brand>(),
      vendors: new Array<Vendor>(),
      customPriceTypes: new Array<CustomPriceType>(),
      attributes: new Array<any>(),
      attributeVariants: [] as Array<any>,
      itemVariants: [] as Array<Item>,
      oldVariants: [] as Array<Item>,
      oldListPriceAmount: null,
      oldSalePriceAmount: null,
      oldCostAmount: null,
      oldCustomPrices: new Map(),
      mounted: false,

      error: { title: '', body: '' },
      isSubmitting: false,

      // Simple dirty flag for unsaved changes
      formIsDirty: false,
      isInitializing: true, // Flag to prevent watchers from triggering during initial load

    }
  },
  computed: {
    //<-- Added
    clonedAttributes: function () {
      return JSON.parse(JSON.stringify(this.attributeVariants))
    }
  },
  watch: {
    // Mark form as dirty when any field changes (but not during initialization)
    item: {
      handler() {
        if (!this.isInitializing) {
          this.formIsDirty = true
        }
      },
      deep: true
    },
    itemVariants: {
      handler() {
        if (!this.isInitializing) {
          this.formIsDirty = true
        }
      },
      deep: true
    },
    attributeVariants: {
      handler() {
        if (!this.isInitializing) {
          this.formIsDirty = true
        }
      },
      deep: true
    },
    selectedTags: {
      handler() {
        if (!this.isInitializing) {
          this.formIsDirty = true
        }
      }
    },

    clonedAttributes: {
      handler(newValue, oldValue) {
        // Don't mark as dirty during variant computation if we're still initializing
        const wasInitializing = this.isInitializing
        this.computeVariants(newValue, oldValue)
        // If we were initializing before computeVariants, we still are after
        // This prevents computeVariants' mutations from marking the form dirty
      }
    },
    'item.name': {
      handler(newValue, _) {
        this.itemVariants.map((iv) => {
          iv.name = `${newValue}: ${iv.options.attributes.join(', ')}`
          return iv
        })
      }
    },
    'item.disabled': {
      handler(newValue, _) {
        this.itemVariants.map((iv) => {
          iv.disabled = newValue
          return iv
        })
      }
    },
    'item.priceType': {
      handler(newValue, _) {
        this.itemVariants.map((iv) => {
          iv.priceType = newValue
          return iv
        })
      }
    },
    'item.isSerialized': {
      handler(newValue, _) {
        if (newValue) {
          // When item is serialized, automatically disable "sell out of stock"
          // since serialized items should be tracked individually
          this.item.sellOutOfStock = false
        }
      }
    }
  },

  async beforeMount() {
    this.selectedTags = this.item.itemTags.map((tag) => tag.id)

    // get categories, brands, vendors, tags for the dropdown menu
    try {
      // const customPriceTypesResp = await this.$http.get<CustomPriceType[]>(CustomPriceType.ENDPOINT)
      // this.customPriceTypes = customPriceTypesResp.map((cat) => CustomPriceType.from(cat))
      //
      // const currentItemPrices = this.item.customPrices || []
      // const newItemPrices = [] as Array<ItemCustomPrice>
      // this.customPriceTypes.forEach((cp) => {
      //   const t = currentItemPrices.filter((i) => i.id == cp.id)
      //   if (t.length) {
      //     newItemPrices.push({
      //       id: cp.id,
      //       priceAmount: t[0].priceAmount,
      //       priceName: cp.enName,
      //       customPriceType: cp
      //     } as ItemCustomPrice)
      //   } else {
      //     newItemPrices.push({
      //       id: cp.id,
      //       priceAmount: 0,
      //       priceName: cp.enName,
      //       customPriceType: cp
      //     } as ItemCustomPrice)
      //   }
      // })
      //
      // this.item.customPrices = newItemPrices

      // let url = this.$http.BASE_URL + `/inventory/categories`;
      const catResp = await this.$http.get<Category[]>(Category.ENDPOINT)
      this.categories = catResp.map((cat) => Category.from(cat))
      // build categoriesTree where loop through categories and use parentCategory.id to build the tree
      this.buildCategoriesTree()
      

      const tagResp = await this.$http.get<Tag[]>(Tag.ENDPOINT)
      this.tags = tagResp.map((tag) => Tag.from(tag))

      const brandResp = await this.$http.get<Brand[]>(Brand.ENDPOINT)
      this.brands = brandResp.map((brand) => Brand.from(brand))

      const vendorResp = await this.$http.get<Vendor[]>(Vendor.ENDPOINT)
      this.vendors = vendorResp.map((vendor) => Vendor.from(vendor))

      const attributesResp = await this.$http.get<Attribute[]>(Attribute.ENDPOINT)
      this.attributes = attributesResp.map((attribute) => Attribute.from(attribute))

      this.attributes.forEach((attribute) => {
        this.attributeVariants.push({ ...attribute, values: [] })
      })

      this.attributeVariants.forEach((av) => {
        // Collect all attribute values with their creation info
        const valuesWithCreationInfo: Array<{
          value: string
          readonly: boolean
          createdAt?: Date | string
          updatedAt?: Date | string
          variantCreatedAt?: Date | string
        }> = []
        
        
        this.item.variants.forEach((variant) => {
          variant.itemAttributes.forEach((itemAttribute) => {
            if (itemAttribute.attributeID === av.id) {
              // Check if we already have this value
              const existingIndex = valuesWithCreationInfo.findIndex(
                existing => existing.value === itemAttribute.attributeValue
              )
              
              if (existingIndex === -1) {
                // New value - add with creation info
                valuesWithCreationInfo.push({
                  value: itemAttribute.attributeValue,
                  readonly: true,
                  createdAt: itemAttribute.createdAt,
                  updatedAt: itemAttribute.updatedAt,
                  variantCreatedAt: variant.createdAt // Use variant's creation date as fallback
                })
              } else {
                // Value exists - keep the earliest creation date
                const existing = valuesWithCreationInfo[existingIndex]
                const existingDate = new Date(existing.createdAt || existing.variantCreatedAt || '1970-01-01')
                const newDate = new Date(itemAttribute.createdAt || variant.createdAt || '1970-01-01')
                
                if (newDate < existingDate) {
                  existing.createdAt = itemAttribute.createdAt
                  existing.updatedAt = itemAttribute.updatedAt
                  existing.variantCreatedAt = variant.createdAt
                }
              }
            }
          })
        })
        
        // Sort by actual creation order (earliest first)
        valuesWithCreationInfo.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.variantCreatedAt || '1970-01-01')
          const dateB = new Date(b.createdAt || b.variantCreatedAt || '1970-01-01')
          return dateA.getTime() - dateB.getTime()
        })
        
        // Set the sorted values
        av.values = valuesWithCreationInfo
      })

      // Sort item variants by creation date as well
      if (this.item.variants && this.item.variants.length > 0) {
        this.item.variants.sort((a, b) => {
          const dateA = new Date(a.createdAt || '1970-01-01')
          const dateB = new Date(b.createdAt || '1970-01-01')
          return dateA.getTime() - dateB.getTime()
        })
      }

      console.log(this.attributeVariants)
    } catch (error) {
      this.error.title = error?.title
      this.error.body = error?.body
    }
  },
  async mounted() {
    if (this.item.id && this.item.hasVariants) {
      this.itemVariants = this.item.variants

      const customPriceTypesResp = await this.$http.get<CustomPriceType[]>(CustomPriceType.ENDPOINT)
      this.customPriceTypes = customPriceTypesResp.map((cat) => CustomPriceType.from(cat))

      this.itemVariants.map((item) => {
        // const currentItemPrices = item.customPrices || []
        // const newItemPrices = [] as Array<ItemCustomPrice>
        // this.customPriceTypes.forEach((cp) => {
        //   const t = currentItemPrices.filter((i) => i.id == cp.id)
        //   if (t.length) {
        //     newItemPrices.push({
        //       id: cp.id,
        //       priceAmount: t[0].priceAmount,
        //       priceName: cp.enName,
        //       customPriceType: cp
        //     } as ItemCustomPrice)
        //   } else {
        //     newItemPrices.push({
        //       id: cp.id,
        //       priceAmount: 0,
        //       priceName: cp.enName,
        //       customPriceType: cp
        //     } as ItemCustomPrice)
        //   }
        // })
        //
        // item.customPrices = newItemPrices
        return item
      })
    }
    this.oldVariants = this.itemVariants

    this.mounted = true

    // Add beforeunload event listener for page reload/close
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    // Use nextTick and a delay to ensure all watchers and DOM updates have finished
    // This prevents the form from being marked as dirty during initial load
    // The timeout accounts for all async operations and cascading watchers
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitializing = false
      }, 500)
    })
  },

  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    handleBeforeUnload(e: BeforeUnloadEvent) {
      if (this.formIsDirty) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    },

    updateSalePrice(e: any) {
      if (this.item.salePriceAmount) {
        return
      }
      /* @ts-ignore */
      this.$refs.salePriceAmountRef.updateValue(this.item.listPriceAmount)
    },
    updateSelectedTags() {
      this.item.itemTags = this.selectedTags.map((tagID) => Tag.from({ id: tagID }))
    },

    handleImage(target: HTMLInputElement) {
      // won't work!!!!! need to change this to a put request
      if (target.files?.length) {
        this.item.imageURL = target.files[0].path
        this.item.imageType = target.files[0].type
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm

      this.selectedTags = new Array<string | undefined>()
      this.item = new Item()
      this.attributeVariants = [];
      this.attributes.forEach((attribute) => {
        this.attributeVariants.push({ ...attribute, values: [] })
      })
      this.$globalEmitter.emit('money-input-reset')
      form.resetFields()

      // Reset dirty flag after clearing form
      this.formIsDirty = false
    },

    async validateSubmit(formName: string) {
      let toUploadVariantImages = [];
      let qunatityMismatchVariantError = false;

      if (!this.item.itemBrand.id) {
        this.item.itemBrand.name = "";
        this.item.itemBrand.createdAt = null;
        this.item.itemBrand.updatedAt = null;
        this.item.itemBrand.externalID = "";
        this.item.itemBrandID = null;
      }

      if (!this.item.itemCategory.id) {
        this.item.itemCategory.name = "";
        this.item.itemCategory.createdAt = null;
        this.item.itemCategory.updatedAt = null;
        this.item.itemCategory.externalID = "";
        this.item.itemCategory.parentID = "";
        this.item.itemCategoryID = null;
      }

      if (!this.item.vendor.id) {
        this.item.vendor = new Vendor();
        this.item.vendorID = null;
      }

      // attach the price and cost values
      this.item.listPriceAmount = this.item.listPriceDinero?.getAmount()
      this.item.listPriceCurrency = this.item.listPriceDinero?.getCurrency()
      this.item.listPricePrecision = this.item.listPriceDinero?.getPrecision()

      this.item.salePriceAmount = this.item.salePriceDinero?.getAmount()
      this.item.salePriceCurrency = this.item.salePriceDinero?.getCurrency()
      this.item.salePricePrecision = this.item.salePriceDinero?.getPrecision()

      this.item.wholesalePriceAmount = this.item.wholesalePriceDinero?.getAmount()
      this.item.wholesalePriceCurrency = this.item.wholesalePriceDinero?.getCurrency()
      this.item.wholesalePricePrecision = this.item.wholesalePriceDinero?.getPrecision()

      this.item.costAmount = this.item.costDinero?.getAmount()
      this.item.costCurrency = this.item.costDinero?.getCurrency()
      this.item.costPrecision = this.item.costDinero?.getPrecision()

      let barcodes: string[] = [];
      if (this.item.barcode) {
        barcodes.push(this.item.barcode);
      }

      // Add barcodes from itemBarcodes array
      if (this.item.itemBarcodes && this.item.itemBarcodes.length > 0) {
        this.item.itemBarcodes.forEach(b => {
          if (b.barcode) {
            barcodes.push(b.barcode);
          }
        });
      }

      if (this.item.itemPacks) {
        this.item.itemPacks.map(itemPack => {
          if (itemPack.barcode) {
            barcodes.push(itemPack.barcode);
          }
          itemPack.qty = Number.parseInt(itemPack.qty);
          itemPack.listPriceAmount = itemPack.listPriceDinero?.getAmount()
          itemPack.listPriceCurrency = itemPack.listPriceDinero?.getCurrency()
          itemPack.listPricePrecision = itemPack.listPriceDinero?.getPrecision()

          itemPack.salePriceAmount = itemPack.salePriceDinero?.getAmount()
          itemPack.salePriceCurrency = itemPack.salePriceDinero?.getCurrency()
          itemPack.salePricePrecision = itemPack.salePriceDinero?.getPrecision()

          if (itemPack.itemPackCustomPrices) {
            itemPack.itemPackCustomPrices.map(itemPackCustomPrice => {
              itemPackCustomPrice.salePriceAmount = itemPackCustomPrice.salePriceDinero?.getAmount()
              itemPackCustomPrice.salePriceCurrency = itemPackCustomPrice.salePriceDinero?.getCurrency()
              itemPackCustomPrice.salePricePrecision = itemPackCustomPrice.salePriceDinero?.getPrecision()

              return itemPackCustomPrice;
            })
          }

          return itemPack;
        })
      }

      if (this.item.itemCustomPrices) {
        this.item.itemCustomPrices.map(itemCustomPrice => {
          itemCustomPrice.salePriceAmount = itemCustomPrice.salePriceDinero?.getAmount()
          itemCustomPrice.salePriceCurrency = itemCustomPrice.salePriceDinero?.getCurrency()
          itemCustomPrice.salePricePrecision = itemCustomPrice.salePriceDinero?.getPrecision()

          return itemCustomPrice;
        })
      }
      this.itemVariants.map(iv => {
        if (iv.barcode) {
          barcodes.push(iv.barcode);
        }

        // Add barcodes from variant itemBarcodes array
        if (iv.itemBarcodes && iv.itemBarcodes.length > 0) {
          iv.itemBarcodes.forEach(b => {
            if (b.barcode) {
              barcodes.push(b.barcode);
            }
          });
        }

        if (!iv.itemBrand.id) {
          iv.itemBrand.name = "";
          iv.itemBrand.createdAt = null;
          iv.itemBrand.updatedAt = null;
          iv.itemBrand.externalID = "";
          iv.itemBrandID = null;
        }

        if (!iv.itemCategory.id) {
          iv.itemCategory.name = "";
          iv.itemCategory.createdAt = null;
          iv.itemCategory.updatedAt = null;
          iv.itemCategory.externalID = "";
          iv.itemCategory.parentID = "";
          iv.itemCategoryID = null;
        }

        toUploadVariantImages = [...toUploadVariantImages, iv.toUploadImages]
        if (iv.itemCustomPrices) {
          iv.itemCustomPrices.map(itemCustomPrice => {
            itemCustomPrice.salePriceAmount = itemCustomPrice.salePriceDinero?.getAmount()
            itemCustomPrice.salePriceCurrency = itemCustomPrice.salePriceDinero?.getCurrency()
            itemCustomPrice.salePricePrecision = itemCustomPrice.salePriceDinero?.getPrecision()

            return itemCustomPrice;
          })
        }

        if (!iv.requestOnlyQuantityInHand) {
          iv.requestOnlyQuantityInHand = 0;
        }

        if (this.item.isSerialized && iv.requestOnlyQuantityInHand > 0 && iv.requestOnlyQuantityInHand != iv.requestOnlyQuantityInHandItemSerials?.length) {
          qunatityMismatchVariantError = true;
        }

        // validate batch dates for item variants
        if (this.item.isExpirable && iv.requestOnlyQuantityInHand > 0) {
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          
          // Check manufacture date is not in future
          if (iv.requestOnlyManufactureDate) {
            const mfgDate = new Date(iv.requestOnlyManufactureDate)
            mfgDate.setHours(0, 0, 0, 0)
            if (mfgDate > today) {
              this.error.title = `${iv.name}: ${this.t('errManufactureDateFuture')}`
              document.querySelector('*')?.scrollTo({ top: 0 })
              return false;
            }
          }
          
          // Check expiry date is not before manufacture date
          if (iv.requestOnlyManufactureDate && iv.requestOnlyExpiryDate) {
            const mfgDate = new Date(iv.requestOnlyManufactureDate)
            const expDate = new Date(iv.requestOnlyExpiryDate)
            mfgDate.setHours(0, 0, 0, 0)
            expDate.setHours(0, 0, 0, 0)
            if (expDate < mfgDate) {
              this.error.title = `${iv.name}: ${this.t('errExpiryBeforeManufacture')}`
              document.querySelector('*')?.scrollTo({ top: 0 })
              return false;
            }
          }
        }

        if (iv.itemPacks) {
          iv.itemPacks.map(itemPack => {
            if (itemPack.barcode) {
              barcodes.push(itemPack.barcode);
            }
            itemPack.qty = Number.parseInt(itemPack.qty);
            itemPack.listPriceAmount = itemPack.listPriceDinero?.getAmount()
            itemPack.listPriceCurrency = itemPack.listPriceDinero?.getCurrency()
            itemPack.listPricePrecision = itemPack.listPriceDinero?.getPrecision()

            itemPack.salePriceAmount = itemPack.salePriceDinero?.getAmount()
            itemPack.salePriceCurrency = itemPack.salePriceDinero?.getCurrency()
            itemPack.salePricePrecision = itemPack.salePriceDinero?.getPrecision()

            if (itemPack.itemPackCustomPrices) {
              itemPack.itemPackCustomPrices.map(itemPackCustomPrice => {
                itemPackCustomPrice.salePriceAmount = itemPackCustomPrice.salePriceDinero?.getAmount()
                itemPackCustomPrice.salePriceCurrency = itemPackCustomPrice.salePriceDinero?.getCurrency()
                itemPackCustomPrice.salePricePrecision = itemPackCustomPrice.salePriceDinero?.getPrecision()

                return itemPackCustomPrice;
              })
            }

            return itemPack;
          })
        }
      })

      if (!this.item.requestOnlyQuantityInHand) {
        this.item.requestOnlyQuantityInHand = 0;
      }

      // validate quantity in hand serials
      if (this.item.isSerialized && this.item.requestOnlyQuantityInHand > 0 && this.item.requestOnlyQuantityInHand != this.item.requestOnlyQuantityInHandItemSerials?.length) {
        this.error.title = `${localizeFieldName('serialNumbers', inventoryReceivingI18nMessages)} ${this.t('validation.matchingLength')} ${localizeFieldName('quantity', inventoryReceivingI18nMessages)}`
        document.getElementById('main')?.scrollTo({ top: 0 })
        return false;
      }

      // validate batch dates for main item
      if (this.item.isExpirable && this.item.requestOnlyQuantityInHand > 0) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        // Check manufacture date is not in future
        if (this.item.requestOnlyManufactureDate) {
          const mfgDate = new Date(this.item.requestOnlyManufactureDate)
          mfgDate.setHours(0, 0, 0, 0)
          if (mfgDate > today) {
            this.error.title = this.t('errManufactureDateFuture')
            document.getElementById('main')?.scrollTo({ top: 0 })
            return false;
          }
        }
        
        // Check expiry date is not before manufacture date
        if (this.item.requestOnlyManufactureDate && this.item.requestOnlyExpiryDate) {
          const mfgDate = new Date(this.item.requestOnlyManufactureDate)
          const expDate = new Date(this.item.requestOnlyExpiryDate)
          mfgDate.setHours(0, 0, 0, 0)
          expDate.setHours(0, 0, 0, 0)
          if (expDate < mfgDate) {
            this.error.title = this.t('errExpiryBeforeManufacture')
            document.getElementById('main')?.scrollTo({ top: 0 })
            return false;
          }
        }
      }

      if (qunatityMismatchVariantError) {
        this.error.title = `${localizeFieldName('serialNumbers', inventoryReceivingI18nMessages)} ${this.t('validation.matchingLength')} ${localizeFieldName('quantity', inventoryReceivingI18nMessages)}`
        document.querySelector('*')?.scrollTo({ top: 0 })
        return false;
      }


      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.querySelector('*')?.scrollTo({ top: 0 })
          return false
        }

        let blockProceed = false;
        const affectedStocks = [];
        for (const itemStock of this.item.itemStocks) {
          if (itemStock.quantity < 0) {
            affectedStocks.push(itemStock)
          }
        }

        // check name exists
        // try {
        //   const foundedItems = await this.$http.get<Item[]>(Item.ENDPOINT+`/findByName?name=${this.item.name}&id=${this.item.id? this.item.id: ''}`)
        //   if (foundedItems.length) {
        //     await ElMessageBox.confirm(
        //         this.t('itemNameExists'),
        //         this.t('warning'),
        //         {
        //           confirmButtonText: this.t('OK'),
        //           cancelButtonText: this.t('Cancel'),
        //           type: 'warning',
        //           customClass: `${this.$ctx.locale == 'ar'? 'rtl': ''}`
        //         }
        //     )
        //   }
        // } catch (e) {
        //   if (e == 'cancel') {
        //     return ;
        //   }
        // }

        if (affectedStocks.length && this.item.isSerialized) {
          blockProceed = true;
          try {
            await ElMessageBox.confirm(
                h(ItemToSerializedContent, {t: this.t, affectedStocks: affectedStocks}, null),
                this.t('warning'),
                {
                  confirmButtonText: this.t('OK'),
                  cancelButtonText: this.t('Cancel'),
                  type: 'warning',
                  customClass: `${this.$ctx.locale == 'ar'? 'rtl': ''}`
                }
            )
            blockProceed = false;
          } catch (e) {}
        }

        if (blockProceed) {
          return;
        }

        // check if there is a duplicate barcode among variants
        const counts = {};
        for (const code of barcodes) {
          counts[code] = (counts[code] || 0) + 1;
        }

        // Extract duplicates
        const duplicates = Object.keys(counts).filter(code => counts[code] > 1);
        if (duplicates.length) {
          this.error.title = this.t('duplicateBarcodeError')
          this.error.body = duplicates.join(',  ')
          document.querySelector('*')?.scrollTo({top: 0})
          return;
        }

        // Prevent double submission
        if (this.isSubmitting) {
          return false
        }

        this.isSubmitting = true

        try {
          const resp = await this.submitAction({
            item: this.item,
            variants: this.itemVariants,
            images: this.item.toUploadImages,
            variantImages: toUploadVariantImages,
          })

          if (resp) {
            this.error.title = resp?.title
            this.error.body = resp?.body
            document.querySelector('*')?.scrollTo({ top: 0 })
            return false
          } else {
            // Reset dirty flag after successful save
            this.formIsDirty = false

            if (this.clearFormOnSubmit) {
              this.clearForm(formName)
            }

            this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
            this.$router.back()
          }
          return true
        } finally {
          this.isSubmitting = false
        }
      })
    },

    // Function to compute Cartesian product of multiple arrays
    cartesianProduct(...arrays): Array<Array<string>> {
      if (arrays.length == 0) {
        return []
      }
      return arrays.reduce(
        (accumulator: any, currentArray: any) => {
          const result: any = []
          for (const acc of accumulator) {
            for (const item of currentArray) {
              result.push([...acc, item])
            }
          }
          return result
        },
        [[]]
      )
    },
    computeVariants(newValue: Array<any>, oldValue: Array<any>) {
      const newArrays: any = []
      for (const attribute of newValue) {
        if (attribute.values.length)
          newArrays.push(
            attribute.values.map((i) => {
              return { value: i.value, attributeID: attribute.id }
            })
          )
      }

      const oldArrays: any = []
      for (const attribute of oldValue) {
        if (attribute.values.length)
          oldArrays.push(
            attribute.values.map((i) => {
              return { value: i.value, attributeID: attribute.id }
            })
          )
      }

      const newVariants = this.cartesianProduct(...newArrays)
      const oldVariants = this.cartesianProduct(...oldArrays)

      const temp = newVariants.map((variants) => {
        const attributes = variants.map((v) => v.value)
        const item = new Item()
        item.name = `${this.item.name}: ${attributes.join(', ')}`
        item.options = { attributes: attributes }
        item.newAttributes = variants
        item.priceType = this.item.priceType

        item.listPriceAmount = this.item.listPriceDinero?.getAmount() || null
        item.listPriceCurrency = this.item.listPriceDinero?.getCurrency() || this.$ctx.currency
        item.listPricePrecision =
          this.item.listPriceDinero?.getPrecision() || this.$ctx.currencyPrecision

        item.salePriceAmount = this.item.salePriceDinero?.getAmount() || null
        item.salePriceCurrency = this.item.salePriceDinero?.getCurrency() || this.$ctx.currency
        item.salePricePrecision =
          this.item.salePriceDinero?.getPrecision() || this.$ctx.currencyPrecision

        item.wholesalePriceAmount = this.item.wholesalePriceDinero?.getAmount() || null
        item.wholesalePriceCurrency =
          this.item.wholesalePriceDinero?.getCurrency() || this.$ctx.currency
        item.wholesalePricePrecision =
          this.item.wholesalePriceDinero?.getPrecision() || this.$ctx.currencyPrecision

        item.costAmount = this.item.costDinero?.getAmount() || 0
        item.costCurrency = this.item.costDinero?.getCurrency() || this.$ctx.currency
        item.costPrecision = this.item.costDinero?.getPrecision() || this.$ctx.currencyPrecision

        // const currentItemPrices = item.customPrices || []
        // const newItemPrices = [] as Array<ItemCustomPrice>
        // this.customPriceTypes.forEach((cp) => {
        //   const t = currentItemPrices.filter((i) => i.id == cp.id)
        //   if (t.length) {
        //     newItemPrices.push({
        //       id: cp.id,
        //       priceAmount: t[0].priceAmount,
        //       priceName: cp.enName,
        //       customPriceType: cp
        //     } as ItemCustomPrice)
        //   } else {
        //     newItemPrices.push({
        //       id: cp.id,
        //       priceAmount: 0,
        //       priceName: cp.enName,
        //       customPriceType: cp
        //     } as ItemCustomPrice)
        //   }
        // })

        // item.customPrices = newItemPrices

        // item.customPrices = JSON.parse(JSON.stringify(this.item.customPrices)) // make clone

        return item
      })

      temp.forEach((tempItem) => {
        let found = false
        this.itemVariants.forEach((item) => {
          if (
            'attributes' in item.options &&
            this.arraysEqual(item.options.attributes, tempItem.options.attributes)
          ) {
            found = true
          }
        })
        if (!found) {
          this.itemVariants.push(tempItem)
        }
      })

      // manage deleted variants
      const newVariantsAttributes = newVariants.map((nv) => nv.map((i) => i.value))
      const oldVariantsAttributes = oldVariants.map((ov) => ov.map((i) => i.value))
      const deletedValues = this.getDeletedItems(newVariantsAttributes, oldVariantsAttributes)

      deletedValues.forEach((val) => {
        this.itemVariants = this.itemVariants.filter((iv) => {
          if ('attributes' in iv.options && this.arraysEqual(iv.options.attributes, val)) {
            return false
          }
          return true
        })
      })

      if (this.item.id) {
        // add back readonly variants if not exists and remove duplicate variant name
        this.oldVariants.forEach((ov) => {
          let found = false
          this.itemVariants.forEach((iv) => {
            if (iv.id == ov.id) {
              found = true
            }
          })
          this.itemVariants = this.itemVariants.filter((iv) => {
            if (!iv.id && iv.name == ov.name) {
              return false
            }
            return true
          })

          if (!found) {
            this.itemVariants = [ov, ...this.itemVariants]
          }
        })
      }
    },
    arraysEqual(arr1: Array<any>, arr2: Array<any>) {
      if (arr1.length !== arr2.length) {
        return false
      }

      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
          return false
        }
      }

      return true
    },
    getDeletedItems(newValues: any, oldValues: any) {
      // Helper function to compare two arrays
      function arraysEqual(a, b) {
        if (a.length !== b.length) return false
        for (let i = 0; i < a.length; i++) {
          if (a[i] !== b[i]) return false
        }
        return true
      }

      const deletedItems = []

      // Iterate through oldValues to find items not present in newValues
      oldValues.forEach((oldItem) => {
        const isDeleted = !newValues.some((newItem) => arraysEqual(newItem, oldItem))
        if (isDeleted) {
          deletedItems.push(oldItem)
        }
      })

      return deletedItems
    },
    onAttributeValueChanged(attribute: any, { oldValue, newValue }: { oldValue: string, newValue: string }) {
      // Update all itemVariants that use the old value for this attribute
      this.itemVariants.forEach((iv: Item) => {
        // Check existing variants (with itemAttributes from server)
        if (iv.itemAttributes?.length) {
          iv.itemAttributes.forEach((ia: any) => {
            if (ia.attributeID === attribute.id && ia.attributeValue === oldValue) {
              ia.attributeValue = newValue
            }
          })
        }

        // Check new variants (with newAttributes from computeVariants)
        if (iv.newAttributes?.length) {
          iv.newAttributes.forEach((na: any) => {
            if (na.attributeID === attribute.id && na.value === oldValue) {
              na.value = newValue
            }
          })
        }

        // Update options.attributes array
        if (iv.options?.attributes?.length) {
          const idx = iv.options.attributes.indexOf(oldValue)
          if (idx !== -1) {
            // Verify this attribute position corresponds to the right attribute
            // by checking newAttributes or itemAttributes at same index
            let matches = false
            if (iv.newAttributes?.[idx]?.attributeID === attribute.id) {
              matches = true
            } else if (iv.itemAttributes?.[idx]?.attributeID === attribute.id) {
              matches = true
            } else {
              // Fallback: just match by value (less precise but handles edge cases)
              matches = true
            }
            if (matches) {
              iv.options.attributes[idx] = newValue
            }
          }
        }

        // Rebuild variant name
        const attrs = this.getVariantAttributes(iv)
        if (attrs.length) {
          iv.name = `${this.item.name}: ${attrs.map(a => a.value).join(', ')}`
        }
      })
    },

    getVariantAttributes(itemVariant: Item): Array<{ value: string, attributeID: string }> {
      // For existing variants (loaded from server), use itemAttributes
      if (itemVariant.id && itemVariant.itemAttributes?.length) {
        return itemVariant.itemAttributes.map(attr => ({
          value: attr.attributeValue,
          attributeID: attr.attributeID
        }))
      }
      // For new variants, use newAttributes
      if (itemVariant.newAttributes?.length) {
        return itemVariant.newAttributes.map(attr => ({
          value: attr.value,
          attributeID: attr.attributeID
        }))
      }
      // Fallback: derive from options.attributes (no attributeID available)
      if (itemVariant.options?.attributes?.length) {
        return itemVariant.options.attributes.map((val: string) => ({
          value: val,
          attributeID: ''
        }))
      }
      return []
    },

    updatePrice($e: any, key: string) {
      if (key == 'listPriceAmount') {
        this.itemVariants.map((iv) => {
          if (this.oldListPriceAmount == iv.listPriceAmount) {
            iv.listPriceAmount = this.item.listPriceAmount
            iv.listPriceCurrency = this.item.listPriceCurrency || this.$ctx.currency
            iv.listPriceDinero = this.item.listPriceDinero
            iv.listPricePrecision = this.item.listPricePrecision || this.$ctx.currencyPrecision
          }
        })
        this.oldListPriceAmount = this.item.listPriceAmount
      } else if (key == 'salePriceAmount') {
        this.itemVariants.map((iv) => {
          if (this.oldSalePriceAmount == iv.salePriceAmount) {
            iv.salePriceAmount = this.item.salePriceAmount
            iv.salePriceCurrency = this.item.salePriceCurrency || this.$ctx.currency
            iv.salePriceDinero = this.item.salePriceDinero
            iv.salePricePrecision = this.item.salePricePrecision || this.$ctx.currencyPrecision
          }
        })
        this.oldSalePriceAmount = this.item.salePriceAmount
      } else if (key == 'costAmount') {
        this.itemVariants.map((iv) => {
          if (this.oldCostAmount == iv.costAmount) {
            iv.costAmount = this.item.costAmount
            iv.costCurrency = this.item.costCurrency || this.$ctx.currency
            iv.costDinero = this.item.costDinero
            iv.costPrecision = this.item.costPrecision || this.$ctx.currencyPrecision
          }
        })
        this.oldCostAmount = this.item.costAmount
      }
    },
    updateVariantCustomPrice($e, cp: ItemCustomPrice) {
      if (!this.mounted) return
      this.itemVariants.map((iv) => {
        iv.customPrices.map((ivcp) => {
          if (
            ivcp.id == cp.id &&
            (this.oldCustomPrices.get(cp.id) == ivcp.priceAmount ||
              !ivcp.priceAmount ||
              !this.oldCustomPrices.get(cp.id))
          ) {
            ivcp.priceAmount = cp.priceAmount
            ivcp.priceDinero = cp.priceDinero
          }
        })
      })

      this.oldCustomPrices.set(cp.id, cp.priceAmount)
    },
    buildCategoriesTree() {
      interface TreeNode {
        value: string
        label: string
        children?: TreeNode[]
      }
      
      const categoryMap = new Map<string, TreeNode>()
      
      // First, create nodes for all categories
      this.categories.forEach(category => {
        categoryMap.set(category.id, {
          value: category.id,
          label: category.name,
          children: []
        })
      })
      
      // Build the tree structure
      const rootCategories: TreeNode[] = []
      
      this.categories.forEach(category => {
        const node = categoryMap.get(category.id)
        
        if (category.parentID && categoryMap.has(category.parentID)) {
          // This category has a parent, add it to parent's children
          const parent = categoryMap.get(category.parentID)
          if (parent && node) {
            parent.children!.push(node)
          }
        } else {
          // This is a root category (no parent or parent doesn't exist)
          if (node) {
            rootCategories.push(node)
          }
        }
      })
      
      const cleanEmptyChildren = (node: TreeNode): void => {
        if (node.children && node.children.length === 0) {
          delete node.children
        } else if (node.children) {
          node.children.forEach(cleanEmptyChildren)
        }
      }
      
      rootCategories.forEach(cleanEmptyChildren)
      
      this.categoriesTree = rootCategories
    }
  }
})
</script>

<style scoped>
.disabled-active :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #dc2626;
  border-color: #dc2626;
}

h1 {
  @apply text-lg py-3 font-bold;
}

h2 {
  @apply py-3 font-bold;
}

h3 {
  @apply py-3 font-bold;
}

.input-group {
  @apply flex flex-col justify-between w-full;
}

.input-group-row {
  @apply flex justify-between w-full;
}

.input-group-row > label {
  @apply w-6/12;
}

.input-section {
  @apply rounded w-full justify-center;
}

@screen lg {
  .input-section {
    @apply w-7/12;
  }
}

@screen md {
  .input-section {
    @apply flex-row;
  }

  .input-group {
    @apply flex-row;
  }

  .input-group > .input-label {
    @apply w-6/12;
  }
}

.remove-button {
  @apply text-lg;
  color: var(--danger-text-color);
  align-self: center;
  justify-self: center;
  cursor: pointer;
  margin: 0.5rem;
}

.remove-button:hover {
  color: var(--danger-hover-bg-color);
  align-self: center;
  justify-self: center;
}

hr.solid {
  border-top: 1px solid var(--tertiary-bg-color);
}

.hide {
  display: none;
}

.input:hover + .hide {
  display: flex;
  justify-content: center;
}

.danger {
  color: var(--danger-text-color);
  background: var(--danger-bg-color);
}

.success {
  color: var(--success-text-color);
  background: var(--success-bg-color);
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
  text-align: center;
}

tr:nth-child(even) {
  background-color: #f3f3f3;
}
</style>
